import React, { type ReactElement, useEffect, useRef, useState } from 'react';
import { styled as materialStyled } from '@mui/material/styles';
import { Grid, useTheme } from '@mui/material';
import { CommonButtonV1, CommonErrorHelperTextV1 } from 'gemlux-common-components';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { Link, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import * as _ from 'lodash';
import { persistor, RootState, store } from '../../redux/store';
import { authActions } from '../../redux/auth/authSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { validation } from '../../schema/validation';
import VisibilitySvg from '../../assets/img/visibility.svg';
import VisibilityOffSvg from '../../assets/img/visibility-off.svg';
import { FORGOT_PASSWORD_ROUTE, SIGN_UP_ROUTE, VIEW_SUBSCRIPTIONS } from '../../utils/routes';
import { UserStatus } from '../../utils/enums/user-status.enum';
import Otp from '../../components/SignUp/Otp/Otp';
import { organizationActions } from '../../redux/organization/organizationSlice';
import LogoIcon from '../../assets/img/logo-large.svg';
import CustomTextField from '../../components/V1/CustomTextField/CustomTextField';

const CustomBox = materialStyled(Box)(() => ({
  padding: '32px 0px 56px 0px',
}));

const CustomGridContainer = materialStyled(Grid)(() => ({
  maxWidth: '371px',
  backgroundColor: 'white',
  padding: '24px',
  borderRadius: '8px',
  boxShadow: ' 0px 1px 2px 0px rgba(128, 128, 128, 0.12)',
}));

export default function Login(): ReactElement {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const loginBtn = useRef<HTMLButtonElement>(null);

  const componentStatus = useAppSelector((state: RootState) => state.auth.componentStatus);
  const isSubmitting = _.get(componentStatus, 'isSubmitting', false) as boolean;
  const error = _.get(componentStatus, 'error', undefined);
  const userStatus = _.get(componentStatus, 'userStatus', undefined);
  const isLoggedIn = useAppSelector((state: RootState) => state.auth.isLoggedIn);
  const organizationId = useAppSelector((state: RootState) => state.auth.organizationId);

  useEffect(
    () => () => {
      dispatch(authActions.setComponentStatus({ error: '' }));
    },
    [],
  );

  useEffect(() => {
    if (isLoggedIn && organizationId) {
      dispatch(organizationActions.getOrganizationByIdAsync(organizationId));
    }
  }, [isLoggedIn, dispatch, organizationId]);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: yup.object({
      email: validation.emailOrUsername,
      password: validation.passwordForLogin,
    }),

    onSubmit: ({ email, password }) => {
      store.dispatch({ type: 'CLEAR_STORE' });
      persistor.purge().then(() => {
        persistor.persist();
        dispatch(authActions.setComponentStatus({ isSubmitting: true, error: undefined }));
        dispatch(authActions.logInAsync({ email, password, previousPath: location?.state?.previousPath ?? '/' }));
      });
    },
  });

  const enterKeyDownHandler = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      formik.handleSubmit();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', enterKeyDownHandler);
    return () => {
      document.removeEventListener('keydown', enterKeyDownHandler);
    };
  }, []);

  if (userStatus === UserStatus.PENDING) {
    return <Otp email={formik.values.email} />;
  }

  return (
    <CustomGridContainer container>
      <CustomBox display="flex" justifyContent="center" width="100%">
        <Box component="img" src={LogoIcon} />
      </CustomBox>
      <Grid display="flex" justifyContent="center" width="100%" pb="16px">
        <Typography variant="textH3" textAlign="center">
          Log In
        </Typography>
      </Grid>

      <Grid container py="20px">
        <Grid item xs={12} sm={12} display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Grid display="flex" flexDirection="column" gap="5px" width="100%">
            <Typography variant="textBase500">Email Address/Username</Typography>
            <CustomTextField
              height={40}
              fullWidth
              type="text"
              name="email"
              placeholder="Email Address/Username"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={
                formik.touched.email &&
                Boolean(formik.errors.email) && <CommonErrorHelperTextV1 errorText={formik.errors.email ?? ''} />
              }
              variant="outlined"
              FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
            />
          </Grid>
          <Grid item xs={12} sm={12} display="flex" flexDirection="column" gap="5px" width="100%">
            <Grid display="flex" flexDirection="row" justifyContent="space-between">
              <Grid>
                <Typography variant="textBase500">Password</Typography>
              </Grid>
              <Grid>
                {' '}
                <Link style={{ textDecoration: 'none' }} to={FORGOT_PASSWORD_ROUTE}>
                  <Typography variant="textBase500" color={theme.palette.shades.primaryBrightDark}>
                    Forgot
                  </Typography>
                </Link>
              </Grid>
            </Grid>

            <CustomTextField
              height={40}
              name="password"
              placeholder="Password"
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={
                formik.touched.password &&
                Boolean(formik.errors.password) && <CommonErrorHelperTextV1 errorText={formik.errors.password ?? ''} />
              }
              variant="outlined"
              FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseLeave={(event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault()}
                      sx={{
                        backgroundColor: '#E0EDFF',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '8px',
                        padding: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          backgroundColor: theme.palette.secondary.light,
                        },
                      }}
                    >
                      {showPassword ? (
                        <Box component="img" src={VisibilityOffSvg} />
                      ) : (
                        <Box component="img" src={VisibilitySvg} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container py="20px" columnGap="20px" display="flex" flexDirection="column">
        <Box display="flex" flexDirection="column" justifyContent="space-around" gap={3} width="100%">
          <CommonButtonV1
            ref={loginBtn}
            loading={isSubmitting}
            variant="contained"
            fullWidth
            onClick={() => formik.handleSubmit()}
          >
            Login
          </CommonButtonV1>
        </Box>
        {error && (
          <Box display="flex" justifyContent="center" alignItems="center" py="20px">
            <CommonErrorHelperTextV1 errorText={error.toString()} />
          </Box>
        )}
      </Grid>

      <Grid container display="flex" justifyContent="center" py="10px">
        <Typography variant="textBase">
          Don’t have an account?{' '}
          <Link style={{ textDecoration: 'none' }} to={SIGN_UP_ROUTE}>
            <Typography variant="textBase" color={theme.palette.shades.primaryBrightDark}>
              Register now
            </Typography>{' '}
          </Link>
        </Typography>
        <Link style={{ textDecoration: 'none', marginTop: '10px' }} to={VIEW_SUBSCRIPTIONS}>
          <Typography variant="textXS" color={theme.palette.shades.primaryBrightDark}>
            View Subscription Plans
          </Typography>{' '}
        </Link>
      </Grid>
    </CustomGridContainer>
  );
}
